if (!customElements.get('marquee-track')) {
  class MarqueeTrack extends HTMLElement {
    constructor() {
      super();
      this.onResize = this.handleResize.bind(this);
      this.onChange = this.scheduleMeasure.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      if (!this.track) return;

      // The template renders the line twice. Everything past those two is a copy this
      // element added, so it keeps its own reference and re-derives the rest.
      this.baseLines = Array.from(this.track.querySelectorAll('[data-line]'));

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopMediaQuery = window.matchMedia('(min-width: 900px)');
      this.reducedMotion.addEventListener('change', this.onChange);
      this.desktopMediaQuery.addEventListener('change', this.onChange);
      window.addEventListener('resize', this.onResize);
      this.lastWidth = window.innerWidth;
      this.measure();

      // The cards are set in DM Sans. Measuring before the webfont swaps in gives a
      // line that is a few px off, and the loop is built from that number.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.onChange);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onChange);
      if (this.desktopMediaQuery) this.desktopMediaQuery.removeEventListener('change', this.onChange);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    // iOS fires resize whenever the URL bar shows or hides, which only changes the
    // height. Only a width change can alter the line, so everything else is ignored.
    handleResize() {
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      this.scheduleMeasure();
    }

    // One measure per frame at most, however many resize events arrive.
    scheduleMeasure() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    measure() {
      if (!this.track || !this.baseLines || this.baseLines.length < 2) return;

      // Start from the markup the template rendered every time, so a resize down never
      // leaves yesterday's copies behind.
      this.track.querySelectorAll('[data-line][data-generated]').forEach((line) => line.remove());

      if (this.reducedMotion.matches) {
        this.track.removeAttribute('data-animate');
        return;
      }

      const firstRect = this.baseLines[0].getBoundingClientRect();
      const lineWidth = firstRect.width;
      if (!lineWidth) return;

      // One loop travels from the first line to its copy, so the distance includes the
      // gap between them.
      const step = this.baseLines[1].getBoundingClientRect().left - firstRect.left;
      const gap = step - lineWidth;
      const viewport = this.getBoundingClientRect().width;

      /*
        Two lines only loop seamlessly while one line is at least as wide as the band.
        A product with four USPs on a wide screen makes a line that is narrower than
        that, and once the track has slid one line along there is nothing left to fill
        the right-hand side — the empty stretch review round 2 flagged. Copy the line
        until what stays behind after the shift still covers the viewport.
      */
      const needed = Math.min(20, Math.max(2, Math.ceil((viewport + gap) / step) + 1));
      for (let index = this.baseLines.length; index < needed; index += 1) {
        const copy = this.baseLines[0].cloneNode(true);
        copy.setAttribute('data-generated', '');
        copy.setAttribute('inert', '');
        copy.setAttribute('aria-hidden', 'true');
        this.track.appendChild(copy);
      }

      // Constant px/second speed so the perceived speed stays the same regardless of how many cards a merchant adds.
      const speedAttribute = this.desktopMediaQuery.matches ? 'data-speed-desktop' : 'data-speed-mobile';
      const pixelsPerSecond = parseFloat(this.getAttribute(speedAttribute)) || 60;

      // The shift is set in px, not as a percentage: the track can now hold any number
      // of lines and -50% would only ever be right for exactly two.
      this.track.style.setProperty('--marquee-shift', `${step}px`);
      this.track.style.setProperty('--marquee-duration', `${step / pixelsPerSecond}s`);
      this.track.setAttribute('data-animate', '');
    }
  }

  customElements.define('marquee-track', MarqueeTrack);
}
